/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    TablePagination,
    Checkbox,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import { endPoints } from 'pages/dlops-dashboard/api/endpoint';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from 'components/error-fallback';
import AccessToken from '../../components/accessToken/accessToken';
import { ProgressIcon } from '../../style';
import { StyledTableHeader, StyledTableCell, style } from '../style';
import CardList from '../cardList.js';

const ClusterLabelTable = ({
    setViewMode,
    viewMode,
    driftDataSet,
    selectedIds,
    setSelectedIds,
    selectedCluster,
    setSelectedCluster,
    deleteImage,
    selectedIdsURL,
    setSelectedIdsURL,
    setDeletedImage,
    selectedImageCount,
    setSelectedImageCount,
}) => {
    const { getAccessToken, handleTokenError } = AccessToken();
    const [page, setPage] = useState(0);
    const [batchSize] = useState(6);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(batchSize - 1);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [cardData, setCardData] = useState([]);
    const [selectedClusterId, setSelectedClusterId] = useState([]);
    const [selectedClusterName, setSelectedClusterName] = useState('');
    const [selectedImageCluster, setSelectedImageCluster] = useState([]);
    const [isCardLoading, setIsCardLoading] = useState(false);
    const [removedClusterIds, setRemovedClusterIds] = useState([]);
    const [manuallyCheckedRows, setManuallyCheckedRows] = useState([]);
    const [boxHeight, setBoxHeight] = useState('40vh'); // default fallback
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [clusterToDelete, setClusterToDelete] = useState({ id: null, name: '' });
    useEffect(() => {
        const updateHeight = () => {
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;
            if (screenWidth > 1440) {
                const dynamicHeight = screenHeight - 320; // custom offset
                setBoxHeight(dynamicHeight);
            } else {
                setBoxHeight('70vh'); // fallback height for smaller screens
            }
        };

        updateHeight(); // initial call
        window.addEventListener('resize', updateHeight);

        return () => window.removeEventListener('resize', updateHeight);
    }, []);
    const confirmDeleteCluster = () => {
        if (clusterToDelete.id) {
            const clusterStr = String(clusterToDelete.id);
            setRemovedClusterIds((prev) => [...prev, clusterStr]);
            setSelectedCluster((prev) => prev.filter((id) => id !== clusterStr));
            setManuallyCheckedRows((prev) => prev.filter((id) => id !== clusterStr));
            toast.success(`Cluster "${clusterToDelete.name}" successfully removed`, {
                position: 'top-right',
                autoClose: 3000,
            });
        }
        setDeleteConfirmationOpen(false);
        setClusterToDelete({ id: null, name: '' });
    };
    const handleDeleteCluster = (clusterId, clusterName) => {
        setClusterToDelete({ id: clusterId, name: clusterName });
        setDeleteConfirmationOpen(true);
    };
    const columns = [
        { key: 'label', name: 'Cluster Label' },
        { key: 'clusterId', name: 'Cluster ID' },
        { key: 'driftScore', name: 'Drift Score' },
        // { key: 'clusterSize', name: 'Cluster Size' },
        { key: 'production', name: 'Production' },
        // { key: 'baseline', name: 'Baseline' },
        { key: 'selectedImages', name: 'Selected Images' },
        { key: 'actions', name: '' },
    ];
    useEffect(() => {
        if (selectedImageCluster?.length) {
            const imageClusters = [...new Set(selectedImageCluster.map(String))];
            const mergedClusters = [...new Set([...selectedCluster.map(String), ...imageClusters])];
            setSelectedCluster(mergedClusters);
        }
    }, [selectedImageCluster]);

    useEffect(() => {
        if (driftDataSet?.length) {
            const formattedData = driftDataSet.map((item) => {
                const clusterIdStr = String(item.cluster_id);
                const isSelected = selectedCluster.includes(item.cluster_id) || selectedCluster.includes(clusterIdStr);
                const hasImageCount = Object.prototype.hasOwnProperty.call(selectedImageCount, clusterIdStr);

                const selectedImageVal = isSelected && !hasImageCount ? item.production : selectedImageCount[clusterIdStr] ?? 0;
                return {
                    clusterId: item.cluster_id,
                    label: item.label || `Cluster ${item.cluster_id}`,
                    driftScore: item.drift_score,
                    // clusterSize: item.cluster_size,
                    production: item.production,
                    // baseline: item.baseline,
                    selectedImages: selectedImageVal,
                };
            });

            setTableData(formattedData);
        } else {
            setTableData([]);
        }

        setStartIndex(0);
        setEndIndex(100);
        setLoading(false);
    }, [driftDataSet, selectedImageCount, selectedCluster]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = tableData.filter((row) => !removedClusterIds.includes(row.clusterId)).map((row) => String(row.clusterId));
            setSelectedCluster(newSelected);
            setManuallyCheckedRows(newSelected);
        } else {
            setSelectedCluster([]);
            setManuallyCheckedRows([]);
        }
    };

    const handleClick = (clusterId) => {
        const clusterStr = String(clusterId);
        const isSelectedNow = selectedCluster.includes(clusterStr);
        setSelectedCluster((prevSelected) => (isSelectedNow ? prevSelected.filter((id) => id !== clusterStr) : [...prevSelected, clusterStr]));
        setManuallyCheckedRows((prev) => {
            if (isSelectedNow) {
                return prev.filter((id) => id !== clusterStr);
            }
            return [...prev, clusterStr];
        });
    };

    const isSelected = (clusterId) => selectedCluster.includes(String(clusterId));

    const fetchClusterDetail = async (clusterIdParam, clusterLabelParam) => {
        const driftDataSetString = localStorage.getItem('viewDriftDataSetVersion');
        const driftDataSetStorage = JSON.parse(driftDataSetString);
        const modelId = driftDataSetStorage?.model_id;

        const payload = {
            model_id: modelId,
            cluster_label: clusterLabelParam,
            cluster_id: clusterIdParam,
        };

        try {
            setIsCardLoading(true);
            const accessToken = getAccessToken();
            const response = await axios.post(endPoints.getClusterDetails(startIndex, endIndex, modelId), payload, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setCardData(response?.data?.data);
            setViewMode('cards');
        } catch (error) {
            handleTokenError(error, endPoints.getClusterDetails(startIndex, endIndex, modelId), 'post', payload);
            toast.error('Failed to fetch cluster details. Please try again.');
        } finally {
            setIsCardLoading(false);
        }
    };
    const renderTable = () => (
        <Box>
            <Paper
                sx={{
                    ...style.tablePaper,
                    px: '30px',
                    py: 0,
                    height: boxHeight,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {tableData.length > 0 ? (
                    <>
                        {/* Scrollable Table with Sticky Header */}
                        <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            <Table stickyHeader sx={{ minWidth: 650 }}>
                                <TableHead
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            backgroundColor: (themes) => themes.palette.cyan['90'],
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                        },
                                    }}
                                >
                                    <TableRow>
                                        <StyledTableHeader padding="checkbox">
                                            <Checkbox
                                                indeterminate={
                                                    selectedCluster.length > 0 &&
                                                    selectedCluster.length < tableData.filter((row) => !removedClusterIds.includes(row.clusterId)).length
                                                }
                                                checked={
                                                    tableData.filter((row) => !removedClusterIds.includes(row.clusterId)).length > 0 &&
                                                    selectedCluster.length === tableData.filter((row) => !removedClusterIds.includes(row.clusterId)).length
                                                }
                                                onChange={handleSelectAllClick}
                                            />
                                        </StyledTableHeader>
                                        {columns.map((col, idx) => (
                                            <StyledTableHeader key={idx}>{col.name}</StyledTableHeader>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableData
                                        .filter((row) => !removedClusterIds.includes(row.clusterId))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            const isItemSelected = isSelected(row.clusterId);
                                            return (
                                                <TableRow
                                                    key={row.clusterId}
                                                    hover
                                                    selected={false}
                                                    sx={{
                                                        backgroundColor: manuallyCheckedRows.includes(String(row.clusterId)) ? '#f5f5f5' : 'inherit',
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox checked={isItemSelected} onChange={() => handleClick(row.clusterId)} />
                                                    </TableCell>

                                                    <StyledTableCell
                                                        sx={{
                                                            color: manuallyCheckedRows.includes(String(row.clusterId)) ? '#A0A0A0' : '#005CBC',
                                                            cursor: manuallyCheckedRows.includes(String(row.clusterId)) ? 'default' : 'pointer',
                                                        }}
                                                        onClick={
                                                            manuallyCheckedRows.includes(String(row.clusterId))
                                                                ? undefined
                                                                : () => {
                                                                      setSelectedClusterId(row.clusterId);
                                                                      setSelectedClusterName(row.label);
                                                                      fetchClusterDetail(row.clusterId, row.label);
                                                                  }
                                                        }
                                                    >
                                                        {row.label}
                                                    </StyledTableCell>
                                                    <StyledTableCell>{row.clusterId}</StyledTableCell>
                                                    <StyledTableCell>{row.driftScore}</StyledTableCell>
                                                    {/* <StyledTableCell>{row.clusterSize}</StyledTableCell> */}
                                                    <StyledTableCell>{row.production}</StyledTableCell>
                                                    {/* <StyledTableCell>{row.baseline}</StyledTableCell> */}
                                                    <StyledTableCell sx={{ pl: 9 }}>{row.selectedImages}</StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        {manuallyCheckedRows.includes(String(row.clusterId)) && (
                                                            <Tooltip title="Remove Cluster">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteCluster(row.clusterId, row.label)}
                                                                    sx={{
                                                                        color: 'primary.main',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(0, 123, 255, 0.1)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="medium" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </StyledTableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Fixed Pagination */}
                        <Box>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                component="div"
                                count={tableData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography variant="body1" textAlign="center" mt={2}>
                        No Clusters Available
                    </Typography>
                )}
            </Paper>
        </Box>
    );

    const renderContent = () => {
        if (isLoading || isCardLoading) {
            return (
                <Box sx={ProgressIcon.provisioning_wrapper}>
                    <CircularProgress color="inherit" size={16} sx={ProgressIcon.circularProgressColor} />
                    <Typography variant="body2" sx={ProgressIcon.circularProgressColor}>
                        Loading
                    </Typography>
                </Box>
            );
        }

        if (viewMode === 'cards') {
            return (
                <CardList
                    data={cardData}
                    selectedClusterName={selectedClusterName}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    selectedClusterId={selectedClusterId}
                    selectedImageCluster={selectedImageCluster}
                    setSelectedImageCluster={setSelectedImageCluster}
                    setSelectedImageCount={setSelectedImageCount}
                    deleteImage={deleteImage}
                    setDeletedImage={setDeletedImage}
                    selectedIdsURL={selectedIdsURL}
                    setSelectedIdsURL={setSelectedIdsURL}
                    selectedCluster={selectedCluster}
                />
            );
        }

        return renderTable();
    };

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box>{renderContent()}</Box>
            <Dialog
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">Are you sure you want to remove cluster {clusterToDelete.name}?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmationOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteCluster} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </ErrorBoundary>
    );
};

export default ClusterLabelTable;
